import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CSmartTable,
} from '@coreui/react-pro'
import CommunityWinnerModal from './modals/CommunityWinnerModal'
import CommunityRoundWinnerModal from './modals/CommunityRoundWinnerModal'
import CommunityTopTeamsModal from './modals/CommunityTopTeamsModal'
import CommunityPlayerOfRoundModal from './modals/CommunityPlayerOfRoundModal'
import CommunityTopPlayersModal from './modals/CommunityTopPlayersModal'
import CommunityRankingUpdateModal from './modals/CommunityRankingUpdateModal'

const CommunityPostsTable = ({
  posts,
  postType,
  fetchPosts,
  setActivePage,
  setItemsPerPage,
  setColumnFilter,
  setColumnSorter,
  itemsPerPage,
  activePage,
  columnFilter,
  columnSorter,
}) => {
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState([])

  // Define columns based on post_type
  const getColumns = () => {
    const baseColumns = [
      {
        key: 'reactions',
        label: 'Reactions',
        filter: false,
        sorter: false,
        _style: { width: '20%' },
      },
      {
        key: 'created_at',
        label: 'Created Date',
        filter: false,
        _style: { width: '15%' },
      },
      {
        key: 'source',
        label: 'Source',
        filter: false,
        _style: { width: '12%' },
      },
      {
        key: 'show_details',
        label: 'Details',
        _style: { width: '10%' },
        filter: false,
        sorter: false,
      },
    ]

    if (postType === 'winner') {
      return [
        { key: 'payload.full_name', label: 'User Name' },
        { key: 'payload.team_name', label: 'Team Name' },
        { key: 'payload.overall_rank', label: 'Rank', filter: false },
        { key: 'payload.overall_points', label: 'Points', filter: false },
        ...baseColumns,
      ]
    }

    if (postType === 'round_winner') {
      return [
        { key: 'payload.full_name', label: 'Winner Name' },
        { key: 'payload.team_name', label: 'Team Name' },
        { key: 'round_number', label: 'Round', filter: false },
        ...baseColumns,
      ]
    }

    if (postType === 'round_top_teams') {
      return [
        { key: 'title', label: 'Team Name' },
        { key: 'short_description', label: 'Round', filter: false },
        ...baseColumns,
      ]
    }

    if (postType === 'player_of_round') {
      return [
        { key: 'payload.player_name', label: 'Player Name' },
        { key: 'payload.team_name', label: 'Team Name' },
        { key: 'round_number', label: 'Round', filter: false },
        { key: 'payload.round_points', label: 'Points', filter: false },
        ...baseColumns,
      ]
    }

    if (postType === 'round_top_players') {
      return [
        { key: 'title', label: 'Title' },
        { key: 'short_description', label: 'Description' },
        ...baseColumns,
      ]
    }

    if (postType === 'ranking_update') {
      return [
        { key: 'title', label: 'Title' },
        { key: 'short_description', label: 'Description' },
        ...baseColumns,
      ]
    }

    // Default columns
    return [{ key: 'payload.title', label: 'Title' }, ...baseColumns]
  }

  const toggleDetails = (index) => {
    const position = details.indexOf(index)
    let newDetails = details.slice()
    if (position !== -1) {
      newDetails.splice(position, 1)
    } else {
      newDetails = [...details, index]
    }
    setDetails(newDetails)
  }

  const getBadge = (source) => {
    const badges = {
      system: 'success',
      user: 'info',
      admin: 'warning',
    }
    return badges[source] || 'primary'
  }

  // Truncate description to first 100 characters
  const truncateText = (text, length = 100) => {
    if (!text) return ''
    return text.length > length ? text.substring(0, length) + '...' : text
  }

  useEffect(() => {
    fetchPosts()
  }, [activePage, columnFilter, columnSorter, itemsPerPage, fetchPosts])

  const columns = getColumns()

  // Build scopedColumns dynamically
  const scopedColumns = {
    reactions: (item) => {
      const reactionTypes = [
        { type: 'like', icon: '👍', color: 'primary', count: item.like_count || 0 },
        { type: 'fire', icon: '🔥', color: 'danger', count: item.fire_count || 0 },
        { type: 'love', icon: '❤️', color: 'danger', count: item.love_count || 0 },
        { type: 'clap', icon: '👏', color: 'warning', count: item.clap_count || 0 },
        { type: 'wow', icon: '😲', color: 'info', count: item.wow_count || 0 },
      ]

      return (
        <td>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {reactionTypes.map((reaction) =>
              reaction.count > 0 ? (
                <CBadge key={reaction.type} color={reaction.color}>
                  {reaction.icon} {reaction.count}
                </CBadge>
              ) : null,
            )}
          </div>
        </td>
      )
    },
    created_at: (item) => <td>{new Date(item.created_at).toLocaleDateString()}</td>,
    source: (item) => (
      <td>
        <CBadge color={getBadge(item.source)}>{item.source}</CBadge>
      </td>
    ),
    show_details: (item) => {
      return (
        <td className="py-2">
          <CButton
            color="primary"
            variant="outline"
            shape="square"
            size="sm"
            onClick={() => {
              toggleDetails(item.id)
            }}
          >
            {details.includes(item.id) ? 'Hide' : 'View'}
          </CButton>
        </td>
      )
    },
    details: (item) => {
      const renderDetailsByType = () => {
        switch (postType) {
          case 'winner':
            return <CommunityWinnerModal item={item} />
          case 'round_winner':
            return <CommunityRoundWinnerModal item={item} />
          case 'round_top_teams':
            return <CommunityTopTeamsModal item={item} />
          case 'player_of_round':
            return <CommunityPlayerOfRoundModal item={item} />
          case 'round_top_players':
            return <CommunityTopPlayersModal item={item} />
          case 'ranking_update':
            return <CommunityRankingUpdateModal item={item} />
          default:
            return (
              <div>
                <div className="mb-3">
                  <strong>Full Data:</strong>
                </div>
                <pre style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>
                  {JSON.stringify(item.payload, null, 2)}
                </pre>
              </div>
            )
        }
      }

      return (
        <CCollapse visible={details.includes(item.id)}>
          <CCardBody className="bg-light p-4">
            {renderDetailsByType()}
            <div className="mt-4 pt-3 border-top">
              <strong>Reactions Summary:</strong>
              <div style={{ marginTop: '10px' }}>
                <CBadge color="primary" className="me-2">
                  👍 Likes: {item.like_count || 0}
                </CBadge>
                <CBadge color="danger" className="me-2">
                  🔥 Fire: {item.fire_count || 0}
                </CBadge>
                <CBadge color="danger" className="me-2">
                  ❤️ Love: {item.love_count || 0}
                </CBadge>
                <CBadge color="warning" className="me-2">
                  👏 Claps: {item.clap_count || 0}
                </CBadge>
                <CBadge color="info">😲 Wows: {item.wow_count || 0}</CBadge>
              </div>
            </div>
          </CCardBody>
        </CCollapse>
      )
    },
  }

  // Add payload field renderers
  columns.forEach((col) => {
    if (col.key.startsWith('payload.')) {
      const payloadKey = col.key.split('.')[1]
      scopedColumns[col.key] = (item) => {
        const value = item.payload?.[payloadKey]
        return <td>{value !== null && value !== undefined ? value : 'N/A'}</td>
      }
    }
  })

  return (
    <>
      <CSmartTable
        columns={columns}
        columnFilter={{
          external: true,
        }}
        columnSorter={{
          external: true,
        }}
        scopedColumns={scopedColumns}
        items={posts?.data}
        itemsPerPage={itemsPerPage}
        itemsPerPageSelect
        loading={loading}
        pagination={{
          external: true,
        }}
        paginationProps={{
          activePage: activePage,
          pages: Math.ceil(posts?.total / itemsPerPage) || 1,
        }}
        tableProps={{
          hover: true,
          responsive: true,
        }}
        onActivePageChange={(activePage) => setActivePage(activePage)}
        onColumnFilterChange={(filter) => {
          setActivePage(1)
          setColumnFilter(filter)
        }}
        onItemsPerPageChange={(itemsPerPage) => {
          setActivePage(1)
          setItemsPerPage(itemsPerPage)
        }}
        onSorterChange={(sorter) => setColumnSorter(sorter)}
      />
    </>
  )
}

export default CommunityPostsTable
